import {
  Badge,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { crm } from "../../../theme";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { ProcessDeletionViewModel } from "../../../models/Process";
import {
  getProcessDeletions,
  restoreProcess,
} from "../../../services/processService";

interface DeletionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRestored?: () => void;
}

const DeletionsModal = ({ isOpen, onClose, onRestored }: DeletionsModalProps) => {
  const [deletions, setDeletions] = useState<ProcessDeletionViewModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<string>();
  const auth = useAuthUser<any>();
  const isAdmin = !!auth?.admin;
  const toast = useToast();

  const fetchDeletions = async () => {
    setLoading(true);
    try {
      const response = await getProcessDeletions();
      setDeletions(response ?? []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    fetchDeletions();
  }, [isOpen]);

  const handleRestore = async (deletion: ProcessDeletionViewModel) => {
    setRestoringId(deletion.processId);
    try {
      const result = await restoreProcess(deletion.processId);
      if (!result?.success) {
        toast({
          title:
            result?.notifications?.[0] ??
            "Não foi possível restaurar o processo.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      toast({
        title: "Processo restaurado.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      await fetchDeletions();
      onRestored?.();
    } catch {
      toast({
        title: "Erro ao restaurar o processo.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setRestoringId(undefined);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent borderRadius="16px" boxShadow="0 12px 32px rgba(16,24,40,0.18)">
        <ModalHeader borderBottom={crm.border}>Histórico de exclusões</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="24px">
          {loading ? (
            <Box textAlign="center" py="32px">
              <Spinner color="var(--icon-color)" />
            </Box>
          ) : deletions.length === 0 ? (
            <Text color="gray.500">Nenhum processo excluído registrado.</Text>
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Ref. CVF</Th>
                  <Th>Excluído por</Th>
                  <Th>Data</Th>
                  <Th>Motivo</Th>
                  {isAdmin && <Th>Ação</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {deletions.map((deletion) => (
                  <Tr key={`${deletion.processId}-${deletion.deletedAt}`}>
                    <Td>{deletion.cvfRef}</Td>
                    <Td>{deletion.deletedByName ?? "—"}</Td>
                    <Td>
                      {DateTime.fromISO(deletion.deletedAt).toFormat(
                        "dd/LL/yyyy HH:mm",
                      )}
                    </Td>
                    <Td whiteSpace="pre-wrap">{deletion.reason}</Td>
                    {isAdmin && (
                      <Td>
                        {deletion.restored ? (
                          <Badge colorScheme="green">Restaurado</Badge>
                        ) : (
                          <Button
                            size="xs"
                            variant="outline"
                            borderColor="var(--icon-color)"
                            color="var(--icon-color)"
                            isLoading={restoringId === deletion.processId}
                            onClick={() => handleRestore(deletion)}
                          >
                            Restaurar
                          </Button>
                        )}
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DeletionsModal;
